/**
 * Real User Monitoring (RUM) for Core Web Vitals
 * Track INP performance in production per PRD 4.2
 *
 * @see docs/PRD_4.2.md - INP < 200ms requirement
 */

/* global performance */

import { getCurrentScope, onScopeDispose, ref, watch, type Ref } from 'vue'
import { onINP, onLCP, onFID, onCLS, type Metric } from 'web-vitals'

// INP threshold from PRD 4.2 (CRITICAL)
const INP_TARGET_MS = 200

export interface PerformanceConfig {
  enableConsoleLogging?: boolean
  enableAnalytics?: boolean
  inpThreshold?: number
}

/**
 * Initialize Core Web Vitals monitoring
 * Call this in main.ts for production monitoring
 *
 * @param config - Configuration options
 *
 * @example
 * ```typescript
 * import { initPerformanceMonitoring } from '@/utils/performance'
 *
 * if (import.meta.env.PROD) {
 *   initPerformanceMonitoring({ enableConsoleLogging: false, enableAnalytics: true })
 * }
 * ```
 */
export function initPerformanceMonitoring(config: PerformanceConfig = {}): void {
  const {
    enableConsoleLogging = true,
    enableAnalytics = false,
    inpThreshold = INP_TARGET_MS
  } = config

  // Track INP (Interaction to Next Paint) - CRITICAL for PRD 4.2
  onINP((metric: Metric) => {
    const exceeds = metric.value > inpThreshold

    if (enableConsoleLogging) {
      const style = exceeds ? 'color: #DC2626; font-weight: bold' : 'color: #059669'
      console.log(
        `%c[Performance] INP: ${metric.value.toFixed(2)}ms ${exceeds ? '⚠️ EXCEEDS TARGET' : '✓'}`,
        style
      )
    }

    // Alert if exceeding target
    if (exceeds) {
      console.warn(`[Performance] INP exceeds ${inpThreshold}ms target:`, {
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
        id: metric.id,
        entries: metric.entries
      })
    }

    // Send to analytics
    if (enableAnalytics) {
      sendToAnalytics({
        name: 'INP',
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
        id: metric.id
      })
    }
  })

  // Track LCP (Largest Contentful Paint)
  onLCP((metric) => {
    if (enableConsoleLogging) {
      console.log(`[Performance] LCP: ${metric.value.toFixed(2)}ms`)
    }
    if (enableAnalytics) {
      sendToAnalytics({ name: 'LCP', value: metric.value, rating: metric.rating })
    }
  })

  // Track FID (First Input Delay)
  onFID((metric) => {
    if (enableConsoleLogging) {
      console.log(`[Performance] FID: ${metric.value.toFixed(2)}ms`)
    }
    if (enableAnalytics) {
      sendToAnalytics({ name: 'FID', value: metric.value, rating: metric.rating })
    }
  })

  // Track CLS (Cumulative Layout Shift)
  onCLS((metric) => {
    if (enableConsoleLogging) {
      console.log(`[Performance] CLS: ${metric.value.toFixed(3)}`)
    }
    if (enableAnalytics) {
      sendToAnalytics({ name: 'CLS', value: metric.value, rating: metric.rating })
    }
  })

  if (enableConsoleLogging) {
    console.log('[Performance] Core Web Vitals monitoring initialized')
  }
}

/**
 * Send metrics to analytics service
 * TODO: Integrate with Google Analytics 4 or custom analytics
 *
 * @param metric - Performance metric to send
 */
function sendToAnalytics(metric: {
  name: string
  value: number
  rating?: string
  [key: string]: unknown
}): void {
  // Production analytics integration
  if (typeof window !== 'undefined' && 'gtag' in window) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).gtag('event', metric.name, {
      event_category: 'Web Vitals',
      value: Math.round(metric.value),
      metric_rating: metric.rating,
      non_interaction: true
    })
  }

  // Can also send to custom endpoint
  // fetch('/api/analytics/vitals', {
  //   method: 'POST',
  //   body: JSON.stringify(metric)
  // });
}

/**
 * Manual INP measurement for specific interactions
 * Useful for debugging specific calculator actions
 *
 * @param name - Name of the interaction being measured
 * @param fn - Function to measure (can be async)
 * @returns The result of the function
 *
 * @example
 * ```typescript
 * const result = await measureInteraction('Calculate Beam', async () => {
 *   return await calculator.analyze(inputs)
 * })
 * ```
 */
export function measureInteraction<T>(
  name: string,
  fn: () => T | Promise<T>
): T | Promise<T> {
  const start = performance.now()

  const measure = (): void => {
    const duration = performance.now() - start
    console.log(`[Performance] ${name}: ${duration.toFixed(2)}ms`)

    if (duration > INP_TARGET_MS) {
      console.warn(`[Performance] ${name} exceeds INP target (${INP_TARGET_MS}ms)`)
    }
  }

  const result = fn()

  if (result instanceof Promise) {
    return result.finally(measure)
  } else {
    measure()
    return result
  }
}



/**
 * Debounce a ref value and return a readonly debounced ref.
 *
 * @param source - Source ref to debounce
 * @param delayMs - Debounce delay in milliseconds
 */
export function useDebounce<T>(source: Ref<T>, delayMs = 300): Readonly<Ref<T>> {
  const debounced = ref(source.value) as Ref<T>
  let timer: ReturnType<typeof setTimeout> | null = null

  const stop = watch(source, (value) => {
    if (timer) {
      clearTimeout(timer)
    }

    timer = setTimeout(() => {
      debounced.value = value
    }, delayMs)
  })

  if (getCurrentScope()) {
    onScopeDispose(() => {
      stop()
      if (timer) {
        clearTimeout(timer)
      }
    })
  }

  return debounced as Readonly<Ref<T>>
}
